/**
 * Zoom Controls Component
 * Provides zoom functionality for the PDF viewer including
 * zoom in/out buttons, percentage display, fit options, and preset levels
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { ZoomIn, ZoomOut, Maximize, Square, ChevronDown } from 'lucide-react';

interface ZoomControlsProps {
  /** Current zoom level (1.0 = 100%) */
  zoom: number;
  /** Callback when zoom level changes */
  onZoomChange: (zoom: number) => void;
  /** Minimum zoom level (default 0.5) */
  minZoom?: number;
  /** Maximum zoom level (default 2.0) */
  maxZoom?: number;
}

// Preset zoom levels
const ZOOM_PRESETS = [
  { value: 0.5, label: '50%' },
  { value: 0.75, label: '75%' },
  { value: 1.0, label: '100%' },
  { value: 1.25, label: '125%' },
  { value: 1.5, label: '150%' },
  { value: 2.0, label: '200%' },
];

// Zoom step for increment/decrement
const ZOOM_STEP = 0.25;

export function ZoomControls({
  zoom,
  onZoomChange,
  minZoom = 0.5,
  maxZoom = 2.0,
}: ZoomControlsProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const handleZoomIn = useCallback(() => {
    const newZoom = Math.min(zoom + ZOOM_STEP, maxZoom);
    onZoomChange(newZoom);
  }, [zoom, maxZoom, onZoomChange]);

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(zoom - ZOOM_STEP, minZoom);
    onZoomChange(newZoom);
  }, [zoom, minZoom, onZoomChange]);

  const handleFitToWidth = useCallback(() => {
    // Fit to width typically means 100% for most cases
    onZoomChange(1.0);
    setShowDropdown(false);
  }, [onZoomChange]);

  const handleFitToPage = useCallback(() => {
    // Fit to page typically means a smaller zoom to see the whole page
    onZoomChange(0.75);
    setShowDropdown(false);
  }, [onZoomChange]);

  const handlePresetSelect = useCallback(
    (preset: number) => {
      onZoomChange(preset);
      setShowDropdown(false);
    },
    [onZoomChange]
  );

  const toggleDropdown = useCallback(() => {
    setShowDropdown((prev) => !prev);
  }, []);

  // Format zoom percentage for display
  const zoomPercentage = Math.round(zoom * 100);

  return (
    <div className="flex items-center gap-1">
      {/* Zoom out button */}
      <button
        onClick={handleZoomOut}
        disabled={zoom <= minZoom}
        className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        aria-label="Zoom out"
        title="Zoom out"
      >
        <ZoomOut size={18} />
      </button>

      {/* Zoom percentage dropdown */}
      <div ref={dropdownRef} className="relative">
        <button
          onClick={toggleDropdown}
          className="inline-flex items-center gap-1 px-2 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 min-w-[70px] justify-center transition-colors"
          aria-label="Select zoom level"
          aria-expanded={showDropdown}
        >
          {zoomPercentage}%
          <ChevronDown size={14} className={`transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown menu */}
        {showDropdown && (
          <div className="absolute top-full left-0 mt-1 w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 py-1">
            {/* Fit options */}
            <div className="px-1 py-1 border-b border-gray-100 dark:border-gray-700">
              <button
                onClick={handleFitToWidth}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors min-h-[44px] sm:min-h-0"
              >
                <Maximize size={14} />
                Fit to width
              </button>
              <button
                onClick={handleFitToPage}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors min-h-[44px] sm:min-h-0"
              >
                <Square size={14} />
                Fit to page
              </button>
            </div>

            {/* Preset zoom levels */}
            <div className="px-1 py-1">
              {ZOOM_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => handlePresetSelect(preset.value)}
                  className={`w-full px-3 py-2 text-sm text-left rounded transition-colors ${
                    zoom === preset.value
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Zoom in button */}
      <button
        onClick={handleZoomIn}
        disabled={zoom >= maxZoom}
        className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        aria-label="Zoom in"
        title="Zoom in"
      >
        <ZoomIn size={18} />
      </button>
    </div>
  );
}

export default ZoomControls;
